import React, { useState, useEffect } from 'react';
import presetApi from '@services/presetApi.jsx';
import themeAngleMappingApi from '@services/themeAngleMappingApi.jsx';
import '@assets/js/common/table';

const PresetStartSetting = ({ preset }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingAngles, setIsLoadingAngles] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const presetId = searchParams.get('presetId');
    const [presetStartPage, setPresetStartPage] = useState(null);

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        enableStandbyAngle: false,
        standbyAngleId: null,
        isActive: true
    });
    
    // 테마 앵글 목록
    const [themeAngles, setThemeAngles] = useState([]);
    const [angles, setAngles] = useState([]);
    
    // 필터 및 선택 관련
    const [angleFilter, setAngleFilter] = useState('all');
    const [selectAll, setSelectAll] = useState(false);
    
    // 모달 상태
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [currentAngle, setCurrentAngle] = useState(null);

    // 페이지 데이터 로드
    useEffect(() => {
        loadStartPageData();
    }, [presetId]);

    useEffect(() => {
        if(presetStartPage?.themeId
            && presetStartPage?.themePageMasterId)
        {
            loadThemeAngles();
        }
    }, [presetStartPage]);
    
    // 시작 페이지 데이터 로드
    const loadStartPageData = async () => {
        try {
            setIsLoading(true);
            const startPage = await presetApi.getStartPage(presetId);

            setPresetStartPage(startPage);

            setFormData({
                ...startPage,
                enableStandbyAngle: startPage.enableStandbyAngle ?? false,
                standbyAngleId: startPage.standbyAngleId ?? null,
                isActive: startPage.isActive ?? true
            });
        } catch (error) {
            console.error('시작 페이지 데이터 로드 실패:', error);
            // 에러 시 기본값 유지
        } finally {
            setIsLoading(false);
        }
    };
    
    // 테마 앵글 목록 로드 (Greeting 타입만)
    const loadThemeAngles = async () => {
        try {
            setIsLoadingAngles(true);

            console.log(presetStartPage);

            const mappings = await themeAngleMappingApi.getThemeAngleMappings(presetStartPage?.themeId, presetStartPage?.themePageMasterId);

            console.log(mappings);

            const formattedAngles = mappings.map((angle) => ({
                id: angle.id,
                cameraAngleId: angle.cameraAngleId,
                name: angle.angleName,
                type: 'Greeting',
                selected: false,
            }));
            
            setThemeAngles(formattedAngles);
            setAngles(formattedAngles);
            
            // 앵글이 있고 현재 선택된 값이 없으면 첫 번째 앵글 자동 선택
            if (formattedAngles.length > 0 && !formData.standbyAngleId) {
                setFormData(prev => ({
                    ...prev,
                    standbyAngleId: formattedAngles[0].cameraAngleId
                }));
            }
        } catch (error) {
            console.error('테마 앵글 목록 로드 실패:', error);
            setThemeAngles([]);
            setAngles([]);
        } finally {
            setIsLoadingAngles(false);
        }
    };
    
    // 대기 앵글 토글 핸들러
    const handleStandbyAngleToggle = () => {
        const newEnabledState = !formData.enableStandbyAngle;
        
        // 토글을 켤 때 앵글이 있으면 첫 번째 값 자동 선택
        const newStandbyAngleId = newEnabledState && themeAngles.length > 0 && !formData.standbyAngleId
            ? themeAngles[0].cameraAngleId
            : formData.standbyAngleId;
        
        setFormData({
            ...formData,
            enableStandbyAngle: newEnabledState,
            standbyAngleId: newStandbyAngleId
        });
    };
    
    // 대기 앵글 선택 핸들러
    const handleStandbyAngleChange = (e) => {
        const angleId = e.target.value ? parseInt(e.target.value) : null;
        setFormData({
            ...formData,
            standbyAngleId: angleId
        });
    };
    
    // 체크박스 선택 핸들러
    const handleCheckboxChange = (id) => {
        setAngles(prevAngles =>
            prevAngles.map(angle =>
                angle.id === id ? { ...angle, selected: !angle.selected } : angle
            )
        );
    };
    
    // 전체 선택 핸들러
    const handleSelectAll = () => {
        setSelectAll(!selectAll);
        setAngles(prevAngles =>
            prevAngles.map(angle => ({ ...angle, selected: !selectAll }))
        );
    };
    
    // 상세정보 아이콘 클릭 핸들러
    const handleInfoClick = (angle) => {
        setCurrentAngle(angle);
        setShowDetailModal(true);
    };

    // 저장 이벤트 리스너 (페이지 상위 컴포넌트에서 저장 요청 시 실행)
    useEffect(() => {
        const eventName = `savePagePreset_start`;

        const saveHandler = async (event) => {
            if (!preset?.id) {
                alert("프리셋 정보가 없습니다.");
                return;
            }

            try {
                setIsLoading(true);

                const dataToSave = {
                    presetId: event.detail.presetId,
                    enableStandbyAngle: formData.enableStandbyAngle,
                    standbyAngleId: formData.standbyAngleId,
                    isActive: formData.isActive
                };
                
                const response = await presetApi.saveStartPage(event.detail.presetId, dataToSave);
                console.log("시작 페이지 저장 성공:", response);

                // 저장 후 최신 데이터로 화면 갱신
                if (response) {
                    setFormData({
                        ...response,
                        enableStandbyAngle: response.enableStandbyAngle ?? false,
                        standbyAngleId: response.standbyAngleId ?? null,
                        isActive: response.isActive ?? true
                    });
                    setPresetStartPage(response);
                }

            } catch (error) {
                console.error("저장 실패:", error);
                alert("시작 페이지 저장 중 오류가 발생했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        document.addEventListener(eventName, saveHandler);

        return () => {
            document.removeEventListener(eventName, saveHandler);
        };
    }, [preset?.id, formData]);
    
    // 테이블 초기화
    // Modified: 2025-01-27 [현재 시간 추가 필요] - 필터가 주석 처리되어 filterSelector 제거
    useEffect(() => {
        if (window.tableManager) {
            window.tableManager.initTable({
                tableSelector: '.common-table',
                // filterSelector: '#angle-filter', // 필터 주석 처리로 인해 제거
                columnWidthOptions: {
                    equalWidth: false,
                    firstColumnWidth: 5,
                    lastColumnWidth: 5,
                    fixedColumns: {
                        1: 10,  // NO 열
                        2: 25,  // 앵글명 열
                        3: 20,  // 앵글 타입 열
                        4: 15   // 상세정보 열
                    }
                }
            });
        }
    }, [angles]);

    if (isLoading) {
        return <div className="preset-settings">로딩 중...</div>;
    }

    return (
        <div className="preset-settings">
            {/* 대기 앵글 자동 선택 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">대기 앵글 자동 선택</h4>
                <div className="settings-row">
                    <div className="settings-label">대기 앵글</div>
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={formData.enableStandbyAngle}
                                onChange={handleStandbyAngleToggle}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                {formData.enableStandbyAngle && (
                    <div className="option-select-container" style={{marginTop: '10px'}}>
                        <select 
                            className="option-select"
                            value={formData.standbyAngleId || ''}
                            onChange={handleStandbyAngleChange}
                        >
                            {themeAngles.length > 0 ? (
                                themeAngles.map(angle => (
                                    <option key={angle.cameraAngleId} value={angle.cameraAngleId}>
                                        {angle.name} ({angle.type})
                                    </option>
                                ))
                            ) : (
                                <option value="">없음</option>
                            )}
                        </select>
                        <i className="ri-arrow-down-s-line select-arrow"></i>
                    </div>
                )}
            </div>

            {/* 앵글 데이터 섹션 헤더 */}
            {/* Modified: 2025-01-27 [현재 시간 추가 필요] - 임시로 프리셋 필터 주석 처리 (현재 지원하지 않음) */}
            <div className="data-section-header">
                <div className="data-section-title">
                    <h4 className="section-title">앵글목록</h4>
                    {isLoadingAngles && <span className="loading-text">(로딩 중...)</span>}
                </div>
                {/*<div className="filter-dropdown-container">
                    <div className="filter-dropdown">
                        <select 
                            id="angle-filter"
                            value={angleFilter}
                            onChange={(e) => setAngleFilter(e.target.value)}
                        >
                            <option value="all">전체</option>
                            <option value="greeting">Greeting</option>
                        </select>
                    </div>
                </div>*/}
            </div>

            {/* 데이터 테이블 섹션 */}
            <div className="common-table-container">
                <table className="common-table">
                    <thead>
                        <tr>
                            {/*<th>*/}
                            {/*    <div className="checkbox-container">*/}
                            {/*        <input */}
                            {/*            type="checkbox" */}
                            {/*            id="select-all" */}
                            {/*            checked={selectAll}*/}
                            {/*            onChange={handleSelectAll}*/}
                            {/*        />*/}
                            {/*        <label htmlFor="select-all" className="custom-checkbox"></label>*/}
                            {/*    </div>*/}
                            {/*</th>*/}
                            <th>NO</th>
                            <th>앵글명</th>
                            <th>앵글 타입</th>
                            <th>상세정보</th>
                        </tr>
                    </thead>
                    <tbody>
                        {angles.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="text-center">
                                    {isLoadingAngles ? '앵글 목록을 로딩 중입니다...' : '등록된 Greeting 앵글이 없습니다.'}
                                </td>
                            </tr>
                        ) : (
                            angles.map((angle, index) => (
                                <tr key={angle.id}>
                                    {/*<td>*/}
                                    {/*    <div className="checkbox-container">*/}
                                    {/*        <input */}
                                    {/*            type="checkbox" */}
                                    {/*            id={`angle-${angle.id}`}*/}
                                    {/*            checked={angle.selected}*/}
                                    {/*            onChange={() => handleCheckboxChange(angle.id)}*/}
                                    {/*        />*/}
                                    {/*        <label htmlFor={`angle-${angle.id}`} className="custom-checkbox"></label>*/}
                                    {/*    </div>*/}
                                    {/*</td>*/}
                                    <td>{index + 1}</td>
                                    <td>{angle.name}</td>
                                    <td>{angle.type}</td>
                                    <td>
                                        <button className="info-btn" onClick={() => handleInfoClick(angle)}>
                                            <i className="ri-information-line"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* 상세정보 모달 */}
            {showDetailModal && currentAngle && (
                <div className="modal-overlay active" id="angle-detail-modal">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">앵글 상세정보</h3>
                            <button className="modal-close" onClick={() => setShowDetailModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-item-container">
                                <span className="detail-label">앵글명</span>
                                <span className="detail-content">{currentAngle.name}</span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">타입</span>
                                <span className="detail-content">{currentAngle.type}</span>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="modal-btn modal-btn-primary" 
                                onClick={() => setShowDetailModal(false)}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PresetStartSetting;
